/**
 * D-ID knowledge base per scenario. Unlike Tavus (flat documents referenced
 * by id), D-ID nests documents inside a knowledge base and the agent points
 * at the base — so each scenario owns exactly one `knl_...`, created lazily on
 * the first document and never shared with another scenario.
 */
import { did } from "./client";
import type {
  DidKnowledgeDocument,
  ScenarioSourceItem,
} from "@/lib/powtoon-be/types";
import {
  sourceKeyOf,
  sourceLabelOf,
  documentCandidates,
  resolveSourceDownloadUrl,
} from "../sourceKeys";
import { createLogger } from "@/lib/frontend-logger";

const logger = createLogger("did.knowledgeDocuments");

export { sourceKeyOf, sourceLabelOf, documentCandidates };

/** Terminal statuses — nothing left to poll for. */
export function isTerminalDocumentStatus(
  status?: DidKnowledgeDocument["status"],
): boolean {
  return status === "ready" || status === "error";
}

/**
 * Return the scenario's knowledge base id, creating it on first use. Callers
 * must persist the returned id on the scenario block.
 */
export async function ensureKnowledgeBase(input: {
  knowledgeId?: string;
  scenarioName: string;
  scenarioId?: string;
}): Promise<string> {
  if (input.knowledgeId?.trim()) return input.knowledgeId.trim();
  const { knowledge_id } = await did.createKnowledge({
    name: input.scenarioName?.trim() || "Scenario knowledge",
    description: input.scenarioId ? `Powtoon scenario ${input.scenarioId}` : undefined,
  });
  logger.info("did knowledge base created", {
    knowledge_id,
    scenario_id: input.scenarioId,
  });
  return knowledge_id;
}

/**
 * Register one scenario source inside the knowledge base. Called from the
 * editor (explicit "Add to D-ID"), never from the save pipeline — the trainer
 * sees the indexing outcome before committing the scenario.
 */
export async function registerSourceDocument(input: {
  item: ScenarioSourceItem;
  scenarioId?: string;
  scenarioName: string;
  knowledgeId?: string;
}): Promise<{ knowledgeId: string; entry: DidKnowledgeDocument }> {
  const key = sourceKeyOf(input.item);
  if (!key) {
    throw new Error("This source can't be used as a D-ID knowledge document.");
  }
  const documentUrl = await resolveSourceDownloadUrl(input.item, input.scenarioId);
  const knowledgeId = await ensureKnowledgeBase({
    knowledgeId: input.knowledgeId,
    scenarioName: input.scenarioName,
    scenarioId: input.scenarioId,
  });
  const created = await did.addKnowledgeDocument({
    knowledge_id: knowledgeId,
    source_url: documentUrl,
    title: sourceLabelOf(input.item).slice(0, 120),
  });
  logger.info("did knowledge document registered", {
    scenario_id: input.scenarioId,
    knowledge_id: knowledgeId,
    source_key: key,
    document_id: created.document_id,
    status: created.status,
  });
  return {
    knowledgeId,
    entry: {
      source_key: key,
      document_id: created.document_id,
      document_name: created.title ?? sourceLabelOf(input.item),
      document_url: documentUrl,
      status: created.status ?? "processing",
      error_message: created.error_message,
      synced_at: new Date().toISOString(),
    },
  };
}

/**
 * Live status for entries stored on the scenario. Nothing is created or
 * deleted here: an entry D-ID no longer has comes back as `missing` so the
 * editor can offer to register its source again.
 */
export async function verifyDocuments(
  knowledgeId: string,
  entries: DidKnowledgeDocument[],
): Promise<DidKnowledgeDocument[]> {
  if (!knowledgeId) return entries;
  return await Promise.all(
    entries.map(async (entry) => {
      try {
        const doc = await did.getKnowledgeDocument({
          knowledge_id: knowledgeId,
          document_id: entry.document_id,
        });
        if (doc.missing) return { ...entry, status: "missing" as const };
        return {
          ...entry,
          document_name: doc.title ?? entry.document_name,
          status: doc.status ?? entry.status,
          error_message: doc.error_message,
          synced_at: new Date().toISOString(),
        };
      } catch (err) {
        logger.warn("could not verify did knowledge document", {
          document_id: entry.document_id,
          error: (err as Error)?.message,
        });
        return entry;
      }
    }),
  );
}

/** Fresh status straight from D-ID, for the editor's polling loop. */
export async function refreshDocumentStatus(input: {
  knowledgeId: string;
  documentId: string;
}): Promise<{ status: DidKnowledgeDocument["status"]; error_message?: string }> {
  const doc = await did.getKnowledgeDocument({
    knowledge_id: input.knowledgeId,
    document_id: input.documentId,
  });
  if (doc.missing) return { status: "missing" };
  return { status: doc.status ?? "processing", error_message: doc.error_message };
}

/** Delete retired documents. Failures are logged, never fatal. */
export async function deleteDidDocuments(
  knowledgeId: string,
  documentIds: string[],
): Promise<void> {
  if (!knowledgeId) return;
  await Promise.all(
    documentIds.map(async (id) => {
      try {
        await did.deleteKnowledgeDocument({ knowledge_id: knowledgeId, document_id: id });
      } catch (err) {
        logger.warn("could not delete did knowledge document", {
          document_id: id,
          error: (err as Error)?.message,
        });
      }
    }),
  );
}

/**
 * Save-time reconciliation. Documents are already registered by the editor,
 * so this only decides what stays on the scenario and what is retired:
 * entries whose source was removed are dropped and deleted upstream after the
 * scenario is persisted. With no documents left, the base is detached from the
 * agent (`knowledge_id: undefined`) so the prompt knowledge takes over again.
 */
export function reconcileSavedDocuments(input: {
  entries: DidKnowledgeDocument[];
  inputs: ScenarioSourceItem[];
}): {
  knowledge_documents: DidKnowledgeDocument[];
  attach: boolean;
  retiredDocumentIds: string[];
} {
  const liveKeys = new Set(documentCandidates(input.inputs).map((c) => c.key));
  const kept = input.entries.filter(
    (e) => liveKeys.has(e.source_key) && e.status !== "missing",
  );
  const retiredDocumentIds = input.entries
    .filter((e) => e.status !== "missing")
    .filter((e) => !kept.some((k) => k.document_id === e.document_id))
    .map((e) => e.document_id);
  return {
    knowledge_documents: kept,
    // Only attach once at least one document is usable — an empty or failing
    // base makes a grounded agent answer nothing at all.
    attach: kept.some((e) => e.status === "ready" || e.status === "processing"),
    retiredDocumentIds,
  };
}
